import React, { forwardRef, useEffect, useImperativeHandle, useRef, useCallback } from 'react';
import SwipeableCard, { SwipeableCardHandle } from '../base/SwipeableCard';
import { BaseAction, ActionCommand } from '../../../types';
import { useTracking } from '../../../../../context/TrackingContext';
import { EventType } from '../../../../../types/events';
import { getCardContent } from '../../../registry/actionTypeRegistry';
import { ActionType } from '../../../constants';

export interface ActionCardProps {
  action: BaseAction;
  onCommand: (command: ActionCommand) => void;
  customIndex?: number;
  onRegisterCardRef?: (actionId: string, ref: SwipeableCardHandle) => void;
  onUnregisterCardRef?: (actionId: string) => void;
}

// The inner implementation of ActionCard, extracted for error boundary wrapping
export const ActionCardInner = forwardRef<SwipeableCardHandle, ActionCardProps>(
  ({ action, onCommand, customIndex = 0, onRegisterCardRef, onUnregisterCardRef }, ref) => {
    const swipeableCardRef = useRef<SwipeableCardHandle>(null);
    const { trackEvent } = useTracking();
    const hasBeenViewedRef = useRef(false);
    const hasBeenRenderedRef = useRef(false);

    // Forward the ref to access both DOM element and methods
    useImperativeHandle(ref, () => ({
      ...swipeableCardRef.current!,
    }));

    // Register card ref with parent component for resetting on modal cancel
    useEffect(() => {
      if (swipeableCardRef.current && onRegisterCardRef) {
        onRegisterCardRef(action.action_id, swipeableCardRef.current);
      }

      return () => {
        if (onUnregisterCardRef) {
          onUnregisterCardRef(action.action_id);
        }
      };
    }, [action.action_id, onRegisterCardRef, onUnregisterCardRef]);

    // Set up Intersection Observer to detect when card becomes visible
    useEffect(() => {
      // Skip if already marked as rendered or if ref is not available
      if (hasBeenRenderedRef.current || !swipeableCardRef.current) return;

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            // If the card enters the viewport and hasn't been marked as rendered yet
            if (entry.isIntersecting && !hasBeenRenderedRef.current) {
              trackEvent(EventType.ACTION_RENDERED, {
                action_id: action.action_id,
                action_type: action.action_type,
                encounter_id: action.encounter_id || null,
                patient_id: action.patient?.patient_id || null,
              });
              hasBeenRenderedRef.current = true;

              // Once rendered event is fired, we can stop observing
              observer.disconnect();
            }
          });
        },
        {
          root: null, // Use viewport as root
          rootMargin: '0px',
          threshold: 1, // Card is considered visible when 100% is in view
        },
      );

      // Use the explicit domElement property we added to SwipeableCardHandle
      const domElement = swipeableCardRef.current.domElement;

      // Only observe if we have a valid DOM element
      if (domElement) {
        observer.observe(domElement);
      } else {
        console.warn('Could not find DOM element for IntersectionObserver');
      }

      // Cleanup observer on unmount
      return () => {
        observer.disconnect();
      };
    }, [action, trackEvent]);

    // Track first view on hover - memoized
    const handleMouseEnter = useCallback(() => {
      if (!hasBeenViewedRef.current) {
        trackEvent(EventType.ACTION_VIEWED, {
          action_id: action.action_id,
          action_type: action.action_type,
          encounter_id: action.encounter_id || null,
          patient_id: action.patient?.patient_id || null,
        });
        hasBeenViewedRef.current = true;
      }
    }, [action, trackEvent]);

    // Get the appropriate card content component from the registry
    const CardContentComponent = getCardContent(action.action_type as ActionType);

    return (
      <SwipeableCard
        id={action.action_id}
        onCommand={onCommand}
        customIndex={customIndex}
        ref={swipeableCardRef}
        onMouseEnter={handleMouseEnter}
      >
        <CardContentComponent action={action} onCommand={onCommand} />
      </SwipeableCard>
    );
  },
);

// Add display name for better debugging
ActionCardInner.displayName = 'ActionCardInner';
